import { supabase } from '../supabase/client';
import { getBeijingToday } from './timeUtils';
import { categoryStore } from '../store/categoryStore';

export interface PermissionCheckResult {
  hasPermission: boolean;
  expiredAt?: string;
  message: string;
}

export interface UserPermission {
  id: string;
  user_id: string;
  organization: string;
  category: string;
  level_text: string;
  expire_date: string;
  is_active: boolean;
}

/**
 * 分类匹配：兼容 code（如 "scratch"）和 name（如 "图形化编程"）两种值
 * papers.category_type 字段历史数据存在两种格式，user_packages.category 同样存在两种格式
 */
function isCategoryMatch(pkgCategory: string, categoryType: string): boolean {
  if (!categoryType) return true;
  if (pkgCategory === categoryType) return true;
  // 双向转换：传入的 code 转 name，传入的 name 转 code，再比较
  const codeToName = categoryStore.getCategoryNameByCode(categoryType);
  const nameToCode = categoryStore.getCategoryCodeByName(categoryType);
  if (codeToName && codeToName !== categoryType && pkgCategory === codeToName) return true;
  if (nameToCode && nameToCode !== categoryType && pkgCategory === nameToCode) return true;
  return false;
}

export async function checkExamPermission(
  userId: string,
  organizationCode: string,
  categoryType: string,
  level: number | string
): Promise<PermissionCheckResult> {
  try {
    const today = getBeijingToday();
    const levelStr = level?.toString() || '';

    // 权限检查参数

    // 0. 先检查用户是否为教师且拥有对应套餐
    const { data: userProfile } = await supabase
      .from('profiles')
      .select('role')
      .eq('id', userId)
      .maybeSingle();

    if (userProfile?.role === 'teacher') {
      const { data: teacherPackages, error: teacherError } = await supabase
        .from('teacher_packages')
        .select('*')
        .eq('teacher_id', userId)
        .eq('is_active', true)
        .gte('expire_date', today);

      if (teacherError) {
        // 教师套餐查询错误，继续检查用户套餐
      }

      const matchedTeacherPackages = (teacherPackages || []).filter(pkg => {
        const orgMatch = pkg.organization === organizationCode;
        const catMatch = isCategoryMatch(pkg.category, categoryType);
        const levelMatch = pkg.level_text === levelStr;
        return orgMatch && catMatch && levelMatch;
      });

      if (matchedTeacherPackages.length > 0) {
        const earliestExpiry = matchedTeacherPackages
          .sort((a, b) => new Date(a.expire_date).getTime() - new Date(b.expire_date).getTime())[0];

        return {
          hasPermission: true,
          expiredAt: earliestExpiry.expire_date,
          message: '权限验证通过（教师套餐）',
        };
      }
    }

    // 1. 检查 user_packages 表中的权限（包括：student_purchase, admin_grant）
    const { data: userPackages, error: userError } = await supabase
      .from('user_packages')
      .select('*')
      .eq('user_id', userId)
      .eq('is_active', true)
      .gte('expire_date', today);

    if (userError) {
      
      throw userError;
    }

    const matchedPackages = (userPackages || []).filter(pkg => {
      const orgMatch = pkg.organization === organizationCode;
      const catMatch = isCategoryMatch(pkg.category, categoryType);
      const levelMatch = pkg.level_text === levelStr;
      return orgMatch && catMatch && levelMatch;
    });

    if (matchedPackages.length > 0) {
      const earliestExpiry = matchedPackages
        .sort((a, b) => new Date(a.expire_date).getTime() - new Date(b.expire_date).getTime())[0];

      const source = earliestExpiry.package_source || 'student_purchase';
      let sourceText = '权限验证通过';
      if (source === 'admin_grant') sourceText = '权限验证通过（后台开通）';

      return {
        hasPermission: true,
        expiredAt: earliestExpiry.expire_date,
        message: sourceText,
      };
    }

    return {
      hasPermission: false,
      message: '您没有该刷题包的访问权限，请先购买套餐',
    };
  } catch (error) {
    return {
      hasPermission: false,
      message: '权限检查失败，请稍后重试',
    };
  }
}

export async function getUserPermissions(userId: string): Promise<UserPermission[]> {
  try {
    const today = getBeijingToday();

    const { data: userPackages, error: userError } = await supabase
      .from('user_packages')
      .select('*')
      .eq('user_id', userId)
      .eq('is_active', true)
      .gte('expire_date', today);

    if (userError) throw userError;

    const allPermissions: UserPermission[] = [];

    (userPackages || []).forEach(p => {
      allPermissions.push({
        id: p.id,
        user_id: p.user_id,
        organization: p.organization,
        category: p.category,
        level_text: p.level_text,
        expire_date: p.expire_date,
        is_active: p.is_active,
      });
    });

    return allPermissions;
  } catch {
    return [];
  }
}

export function getRemainingDays(expiredAt: string): number {
  const now = new Date();
  const expire = new Date(expiredAt);
  const diff = expire.getTime() - now.getTime();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
}

export function formatRemainingTime(expiredAt: string): string {
  const days = getRemainingDays(expiredAt);
  if (days < 0) return '已过期';
  if (days === 0) return '今天到期';
  if (days === 1) return '明天到期';
  if (days <= 7) return `${days}天后到期`;
  if (days <= 30) return `${Math.floor(days / 7)}周后到期`;
  return `${Math.floor(days / 30)}个月后到期`;
}
